import axios from 'axios';
import Cookies from 'js-cookie';

import config from '../../config';
import { environment } from './environment';
import getMinutes from '../utilities/getMinutes';

const {
  authenticate,
  cookieDomain,
  auth0,
  alm,
  gs,
  drxMain,
} = config;

const tokenUrl = authenticate[environment].token;
const authUrl = authenticate[environment].authorize;
const revokeUrl = authenticate[environment].revoke;
const domainValue = cookieDomain[environment];
const auth0ClientId = auth0[environment].clientId;
const auth0LogoutRedirectUrl = auth0[environment].logoutRedirectUrl;
const auth0CustomDomain = auth0[environment].customDomain;

const LOGOUT_DELAY_MS = 3000;

/**
 * Utility: Wait for a given delay
 */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Utility: Redirect after optional delay
 */
async function redirectTo(url, delay = LOGOUT_DELAY_MS) {
  if (typeof window === 'undefined') {
    console.warn(
      '[redirect] Skipping redirect (window is undefined). Target:',
      url,
    );
    return;
  }
  console.log(
    `[redirect] Waiting ${delay}ms before redirecting to:`,
    url,
  );
  await wait(delay);
  window.location.replace(url);
}

/**
 * Utility: Clear all authentication cookies
 */
function clearLoggedInUser() {
  console.log('[clearLoggedInUser] Clearing user cookies...');
  const cookiesToRemove = [
    'currentUser',
    'accessToken',
    'appSession.0',
    'appSession.1',
    'currentUserAttributes',
  ];
  cookiesToRemove.forEach((cookie) =>
    Cookies.remove(cookie, { domain: domainValue }),
  );
}

/**
 * Utility: Trigger logout from all provider endpoints
 */
function logoutFromProviders(logoutUrls) {
  if (typeof window === 'undefined' || !window.document) {
    console.warn(
      '[logoutFromProviders] Skipping provider logout (window/document undefined)',
    );
    return;
  }

  console.log('[logoutFromProviders] Logging out from providers...');
  logoutUrls.forEach((url) => {
    console.log('[logoutFromProviders] Logging out from URL:', url);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
  });
}

/**
 * Main: Logout
 */
async function logout(country = 'us', lng = 'en') {
  console.log('[logout] Initiating logout process...');
  const token = authenticationService.currentUserValue;
  console.log('[logout] Current user token:', token);

  const logoutUrls = [
    `${auth0CustomDomain}/v2/logout?client_id=${auth0ClientId}`,
    `${alm[environment].host}/api/auth/logout`,
    `${gs[environment].host}/${country}/${lng}/${gs[environment].logoutPath}?origin=${drxMain[environment]}`,
  ];

  const referrerUrl = Cookies.get('referrerUrl');
  const finalRedirectUrl = referrerUrl || auth0LogoutRedirectUrl;

  if (token) {
    try {
      console.log('[logout] Revoking token...');
      const response = await axios.post(revokeUrl, { token });
      console.log(
        '[logout] Token revoked successfully:',
        response.data,
      );
    } catch (error) {
      console.error('[logout] Error revoking token:', error);
    }

    clearLoggedInUser();
    logoutFromProviders(logoutUrls);
    await redirectTo(finalRedirectUrl);
  } else {
    console.warn(
      '[logout] No token found. Redirecting to Auth0 logout...',
    );
    await redirectTo(auth0LogoutRedirectUrl);
  }
}

/**
 * Main: Renew
 */
function renew() {
  console.log('[renew] Renewing user session...');
  clearLoggedInUser();
  if (typeof window !== 'undefined') {
    console.log('[renew] Redirecting to authorization URL:', authUrl);
    window.location.replace(authUrl);
  } else {
    console.warn(
      '[renew] Skipping redirect (window is undefined). Target:',
      authUrl,
    );
  }
  return null;
}

/**
 * Main: Login
 */
async function login(code) {
  console.log('[login] Logging in with code:', code);
  try {
    const response = await axios.post(tokenUrl, { code });
    console.log('[login] Login response:', response);

    if (response.status !== 200) {
      if ([401, 403].includes(response.status)) {
        console.warn(
          '[login] Unauthorized or forbidden. Logging out...',
        );
        await logout();
      }
      throw new Error(response.message);
    }

    const { idToken, attributes, app } = response.data;
    console.log('[login] User data received:', { idToken, app });

    if (app) {
      console.log('[login] Storing current user token...');
      Cookies.set('currentUser', idToken, {
        domain: domainValue,
        secure: true,
        expires: getMinutes(60),
      });
    }

    console.log('[login] Storing user attributes...');
    Cookies.set('currentUserAttributes', attributes, {
      domain: domainValue,
      secure: true,
      expires: getMinutes(60),
    });

    return { idToken, app };
  } catch (error) {
    console.error('[login] Error logging in:', error);
    throw error;
  }
}

/**
 * Public API
 */
const authenticationService = {
  login,
  logout,
  renew,
  updateCurrentUser(user) {
    console.log('[updateCurrentUser] Updating current user:', user);
    Cookies.set('currentUser', user, {
      domain: domainValue,
      secure: true,
      expires: getMinutes(60),
    });
  },
  get currentUserValue() {
    const currentUser = Cookies.get('currentUser');
    console.log(
      '[currentUserValue] Current user value:',
      currentUser,
    );
    return currentUser;
  },
};

export default authenticationService;
export { authenticationService };
